/* ==========================================================================
   VITALIS: theme core script (assets/vitalis.js)
   Ported from the approved mockup (prototype/assets/app.js) minus its hash router:
   pages are real Shopify URLs now. Owned by the core; page sections never edit this
   file. They call the public API (window.Vitalis, end of file) or use the data hooks:

     data-action="cart"          open the cart drawer (a link to /cart without JS)
     data-action="talk"          open the Talk sheet; data-about="<context line>" and
                                 data-talk-focus="whatsapp|call|write" are optional
     data-action="cart-add"      add data-variant-id (and data-quantity) to the cart
     data-action="dialog-close"  close the open drawer or sheet
     form[action$="/cart/add"]   product forms post over AJAX and open the drawer
                                 (opt out with data-no-ajax)
     [data-reveal]               fades up once when scrolled into view
     [data-lift] / [data-hl]     links that show the pressed look on touch first

   Events on document: "vitalis:cart:updated" (detail: the cart JSON),
   "vitalis:layer:open" and "vitalis:layer:close" (detail: { el }).
   ========================================================================== */
(function () {
  "use strict";

  var d = document;
  var html = d.documentElement;
  var main = d.getElementById("MainContent");
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var KEYS = { slidein: "vitalis.slidein", loc: "vitalis.loc" };
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var STR = window.VitalisStrings || {};

  function $(sel, root) { return (root || d).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || d).querySelectorAll(sel)); }
  var ESC = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  function esc(v) { return String(v == null ? "" : v).replace(/[&<>"']/g, function (c) { return ESC[c]; }); }
  function t(key, fallback) { return STR[key] || fallback; }

  var media = {
    reduce: window.matchMedia("(prefers-reduced-motion: reduce)"),
    desktop: window.matchMedia("(min-width: 1024px)"),
    fine: window.matchMedia("(hover: hover) and (pointer: fine)")
  };
  function reduced() { return media.reduce.matches; }
  function onMedia(mql, fn) {
    if (mql.addEventListener) mql.addEventListener("change", fn);
    else if (mql.addListener) mql.addListener(fn);
  }
  function emit(name, detail) {
    try { d.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) { /* old browsers */ }
  }

  /* Storage: every access wrapped; the page works when storage throws (private mode). */
  var store = (function () {
    var mem = {};
    return {
      get: function (k) { try { var v = window.localStorage.getItem(k); return v == null ? mem[k] : v; } catch (e) { return mem[k]; } },
      set: function (k, v) { mem[k] = v; try { window.localStorage.setItem(k, v); } catch (e) { /* memory only */ } }
    };
  })();

  /* Polite announcement for screen readers (cart adds, errors). */
  var statusTimer = null;
  function announce(msg) {
    var s = d.getElementById("status");
    if (!s) return;
    clearTimeout(statusTimer);
    s.textContent = "";
    if (msg) statusTimer = setTimeout(function () { s.textContent = msg; }, 40);
  }

  /* ========================================================================
     1. LAYERS: one chassis for the cart drawer, the Talk sheet, page sheets and the
     mobile menu. Focus trap, Esc, scrim, inert background, scroll lock, focus return.
     The background is every child of <body> that does not hold the open layer (or a
     kept element), so Shopify's section wrappers need no special casing.
     ======================================================================== */
  var layers = (function () {
    var scrim = d.getElementById("scrim");
    var current = null;
    var FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function focusables(root) {
      return $$(FOCUSABLE, root).filter(function (el) {
        return !el.closest("[hidden]") && !el.closest("[inert]") && el.getClientRects().length > 0;
      });
    }
    function setBackgroundInert(on, keep) {
      Array.prototype.forEach.call(d.body.children, function (n) {
        if (n === scrim || n.tagName === "SCRIPT" || n.tagName === "TEMPLATE" || n.tagName === "STYLE") return;
        var held = on && keep.some(function (k) { return n === k || n.contains(k); });
        if (on && !held) n.setAttribute("inert", "");
        else n.removeAttribute("inert");
      });
    }

    function open(el, o) {
      o = o || {};
      if (!el) return;
      if (current && current.el === el) return;
      if (current) close({ restore: false, instant: true });
      current = {
        el: el, opener: o.opener || d.activeElement, trap: o.trap || [el],
        useScrim: o.scrim !== false && !!scrim, duration: o.duration || 450,
        onClose: o.onClose, afterClose: o.afterClose
      };
      setBackgroundInert(true, [el].concat(o.keep || []));
      html.classList.add("is-locked");
      el.hidden = false;
      if (current.useScrim) scrim.hidden = false;
      void el.offsetWidth;                       // commit the closed state so the transition runs
      el.classList.add("is-open");
      if (current.useScrim) scrim.classList.add("is-open");
      var target = o.focus || el;
      target.focus({ preventScroll: true });
      emit("vitalis:layer:open", { el: el });
    }

    function close(o) {
      o = o || {};
      if (!current) return;
      var c = current;
      current = null;
      c.el.classList.remove("is-open");
      if (scrim) scrim.classList.remove("is-open");
      setBackgroundInert(false, []);
      html.classList.remove("is-locked");
      if (c.onClose) c.onClose();
      var finish = function () {
        if (current && current.el === c.el) return;              // reopened in the meantime
        c.el.hidden = true;
        if (scrim && (!current || !current.useScrim)) scrim.hidden = true;
        if (c.afterClose) c.afterClose();
      };
      if (o.instant || reduced()) finish(); else setTimeout(finish, c.duration);
      if (o.restore !== false && c.opener && c.opener.isConnected && typeof c.opener.focus === "function") {
        c.opener.focus({ preventScroll: true });
      }
      emit("vitalis:layer:close", { el: c.el });
      setTimeout(function () { slidein.recheck(); }, 120);
    }

    d.addEventListener("keydown", function (e) {
      if (!current) return;
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key !== "Tab") return;
      var list = [];
      current.trap.forEach(function (root) { list = list.concat(focusables(root)); });
      if (!list.length) { e.preventDefault(); return; }
      var i = list.indexOf(d.activeElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); list[list.length - 1].focus(); }
      else if (!e.shiftKey && (i === -1 || i === list.length - 1)) { e.preventDefault(); list[0].focus(); }
    });
    if (scrim) scrim.addEventListener("click", function () { close(); });

    return {
      open: open,
      close: close,
      isOpen: function (el) { return !!current && (!el || current.el === el); },
      focusables: focusables
    };
  })();

  /* ========================================================================
     2. MOBILE MENU: full screen; the header stays on top so the button morphs to X.
     ======================================================================== */
  var menu = (function () {
    function els() { return { el: d.getElementById("menu"), btn: d.getElementById("menu-btn"), hdr: d.getElementById("hdr"), ann: d.getElementById("ann") }; }
    function open(into) {
      var m = els();
      if (!m.el || !m.btn || !m.hdr) return;
      html.style.setProperty("--menu-top", Math.max(0, Math.round(m.hdr.getBoundingClientRect().bottom)) + "px");
      html.classList.add("menu-open");
      m.btn.setAttribute("aria-expanded", "true");
      m.btn.setAttribute("aria-label", m.btn.getAttribute("data-label-close") || "Close menu");
      layers.open(m.el, {
        opener: m.btn, scrim: false, duration: 300,
        keep: [m.hdr, m.ann].filter(Boolean), trap: [m.hdr, m.el],
        focus: (into && into.nodeType === 1 && into) || $(".menu__link", m.el) || m.el,
        onClose: function () {
          m.btn.setAttribute("aria-expanded", "false");
          m.btn.setAttribute("aria-label", m.btn.getAttribute("data-label-open") || "Open menu");
        },
        afterClose: function () { html.classList.remove("menu-open"); }
      });
    }
    function isOpen() { var m = els(); return !!m.el && layers.isOpen(m.el); }
    function close(o) { if (isOpen()) layers.close(o); }
    d.addEventListener("click", function (e) {
      var b = e.target instanceof Element ? e.target.closest("#menu-btn") : null;
      if (!b) return;
      if (isOpen()) close(); else open();
    });
    onMedia(media.desktop, function (e) { if (e.matches) close({ restore: false, instant: true }); });
    return { open: open, close: close, isOpen: isOpen };
  })();

  /* ========================================================================
     3. HEADER STUCK LINE: a 1 px sentinel right before the header's section.
     ======================================================================== */
  var headerObserver = null;
  function observeHeader() {
    var hdr = d.getElementById("hdr");
    if (!hdr || !("IntersectionObserver" in window)) return;
    var wrap = hdr.closest(".shopify-section") || hdr;
    var s = d.getElementById("hdr-sentinel");
    if (!s) {
      s = d.createElement("div");
      s.id = "hdr-sentinel";
      s.className = "hdr-sentinel";
      s.setAttribute("aria-hidden", "true");
      wrap.parentNode.insertBefore(s, wrap);
    }
    if (headerObserver) headerObserver.disconnect();
    headerObserver = new IntersectionObserver(function (entries) {
      var e = entries[0];
      var h = d.getElementById("hdr");
      if (h) h.classList.toggle("is-stuck", !e.isIntersecting && e.boundingClientRect.top < 0);
    });
    headerObserver.observe(s);
  }

  /* ========================================================================
     4. REVEALS. No scroll listeners. The first frame is always complete: only
     elements outside the viewport at init are hidden; elements in view get a
     CSS keyframe that always finishes.
     ======================================================================== */
  var reveal = (function () {
    var io = null;
    function inView(el) {
      var r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    }
    function columnsOf(grid) {
      var c = getComputedStyle(grid).gridTemplateColumns;
      return c && c !== "none" ? c.split(" ").length : 1;
    }
    function ensure() {
      if (!io) {
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            en.target.classList.remove("is-hidden");
            io.unobserve(en.target);
          });
        }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });
      }
      return io;
    }
    function hide(els) {
      if (!els.length) return;
      ensure();
      els.forEach(function (el) { el.style.transition = "none"; el.classList.add("is-hidden"); });
      void d.body.offsetWidth;
      els.forEach(function (el) { el.style.transition = ""; io.observe(el); });
    }
    /* mode "intro": elements in view fade up (70 ms stagger, max 6).
       mode "swap":  a list swap, such as a filter (40 ms stagger, shorter travel). */
    function init(scope, mode) {
      if (reduced() || !("IntersectionObserver" in window)) return;
      var els = $$("[data-reveal]", scope || d).filter(function (el) { return !el.hasAttribute("data-revealed"); });
      els.forEach(function (el) {
        el.setAttribute("data-revealed", "");
        var g = el.parentElement;
        if (g && g.classList.contains("grid")) {
          el.style.setProperty("--ri", Array.prototype.indexOf.call(g.children, el) % columnsOf(g));
        }
      });
      var visible = [], off = [];
      els.forEach(function (el) { (inView(el) ? visible : off).push(el); });
      visible.forEach(function (el, i) {
        if (mode === "swap") { el.style.setProperty("--si", Math.min(i, 6)); el.classList.add("is-swap"); }
        else { el.style.setProperty("--ii", Math.min(i, 6)); el.classList.add("is-in"); }
      });
      hide(off);
    }
    /* Focus reveals at once: Tab can land on a block the observer has not caught yet. */
    d.addEventListener("focusin", function (e) {
      var r = e.target.closest && e.target.closest("[data-reveal].is-hidden");
      if (!r) return;
      r.style.transitionDelay = "0ms";
      r.classList.remove("is-hidden");
      if (io) io.unobserve(r);
    });
    return { init: init, inView: inView };
  })();

  /* ========================================================================
     5. TOUCH PRESS: [data-hl] (hero panels, category tiles) and [data-lift] (product
     links) show their pressed look on touch, and the link follows once the look has
     been on screen PRESS_MS. A swipe that becomes a scroll never flashes it.
     ======================================================================== */
  var press = (function () {
    var PRESS_MS = 180, PRESS_DELAY = 60;
    var el = null, shownAt = 0, timer = 0, going = false;
    function show() { if (el && !shownAt) { el.classList.add("is-pressed"); shownAt = Date.now(); } }
    function clear() {
      clearTimeout(timer);
      if (el) el.classList.remove("is-pressed");
      el = null; shownAt = 0;
    }
    function down(target) {
      clear();
      var a = target instanceof Element ? target.closest("[data-hl], [data-lift]") : null;
      if (!a) return;
      el = a;
      timer = setTimeout(show, PRESS_DELAY);
    }
    function up() {
      if (!el) return;
      show();
      var mine = el;
      setTimeout(function () { if (el === mine) clear(); }, 700);
    }
    var opt = { passive: true };
    if (window.PointerEvent) {
      d.addEventListener("pointerdown", function (e) { if (e.pointerType !== "mouse" && e.isPrimary) down(e.target); }, opt);
      d.addEventListener("pointerup", function (e) { if (e.pointerType !== "mouse") up(); }, opt);
      d.addEventListener("pointercancel", clear, opt);
    } else {
      d.addEventListener("touchstart", function (e) { if (e.touches.length === 1) down(e.target); else clear(); }, opt);
      d.addEventListener("touchend", up, opt);
      d.addEventListener("touchmove", clear, opt);
      d.addEventListener("touchcancel", clear, opt);
    }
    window.addEventListener("pageshow", clear);
    return {
      wait: function (a) { return a === el && shownAt ? Math.max(0, PRESS_MS - (Date.now() - shownAt)) : 0; },
      go: function (href, ms) {
        if (going) return;
        going = true;
        setTimeout(function () { going = false; window.location.href = href; }, ms);
      }
    };
  })();

  /* ========================================================================
     6. CART over the Shopify AJAX Cart API (/cart/add.js, /cart/change.js, /cart.js).
     The drawer and the cart page are Liquid sections marked [data-cart-section]; after
     every change they are re-rendered by the Section Rendering API (bundled into the
     add/change response), so Liquid stays the one source of the markup. Where a host
     does not bundle sections (a local preview), the page is fetched and the same
     sections are read out of it.
     ======================================================================== */
  var cart = (function () {
    var busy = false;

    function sectionIds() {
      return $$("[data-cart-section]").map(function (el) {
        var w = el.closest(".shopify-section");
        return w ? w.id.replace(/^shopify-section-/, "") : null;
      }).filter(Boolean);
    }
    function json(url, body) {
      var o = { method: body ? "POST" : "GET", headers: { Accept: "application/json" }, credentials: "same-origin" };
      if (body) {
        if (body instanceof URLSearchParams) {
          o.body = body.toString();
          o.headers["Content-Type"] = "application/x-www-form-urlencoded";
          o.headers["X-Requested-With"] = "XMLHttpRequest";
        }
        else { o.body = JSON.stringify(body); o.headers["Content-Type"] = "application/json"; }
      }
      return fetch(url, o).then(function (r) {
        return r.text().then(function (txt) {
          var data = null;
          try { data = txt ? JSON.parse(txt) : null; } catch (e) { data = null; }
          if (!r.ok || (data && data.status && data.status >= 400)) {
            var err = new Error((data && (data.description || data.message)) || t("cart_error", "That did not go through. Please try again."));
            err.data = data; throw err;
          }
          return data;
        });
      });
    }
    function get() { return json(ROOT + "cart.js"); }

    /* Swap the inner markup of each cart section, keeping the element itself (its open
       state, focus trap and listeners). */
    function applyHTML(id, markup) {
      var wrap = d.getElementById("shopify-section-" + id);
      if (!wrap || !markup) return;
      var doc = new DOMParser().parseFromString(markup, "text/html");
      var fresh = doc.querySelector("[data-cart-section]");
      var cur = $("[data-cart-section]", wrap);
      if (!fresh || !cur) return;
      cur.innerHTML = fresh.innerHTML;
      reveal.init(cur, "intro");
    }
    function applyPage(markup) {
      var doc = new DOMParser().parseFromString(markup, "text/html");
      sectionIds().forEach(function (id) {
        var w = doc.getElementById("shopify-section-" + id);
        if (w) applyHTML(id, w.innerHTML);
      });
    }
    function render(sections) {
      var ids = sectionIds();
      if (sections && ids.every(function (id) { return id in sections; })) {
        ids.forEach(function (id) { applyHTML(id, sections[id]); });
        return Promise.resolve();
      }
      var url = ROOT + "?sections=" + encodeURIComponent(ids.join(","));
      return fetch(url, { credentials: "same-origin" }).then(function (r) {
        var type = r.headers.get("content-type") || "";
        if (type.indexOf("json") > -1) {
          return r.json().then(function (map) { ids.forEach(function (id) { applyHTML(id, map[id]); }); });
        }
        return fetch(window.location.pathname + window.location.search, { credentials: "same-origin" })
          .then(function (p) { return p.text(); }).then(applyPage);
      });
    }
    function renderCount(c, animate) {
      var n = c ? c.item_count : 0;
      $$("[data-cart-count]").forEach(function (num) {
        var changed = num.textContent !== String(n);
        num.textContent = String(n);
        if (animate && changed && !reduced()) {
          num.classList.remove("is-rolling"); void num.offsetWidth; num.classList.add("is-rolling");
        }
      });
      $$("[data-cart-button]").forEach(function (btn) {
        var label = (n === 1 ? t("cart_count_one", "Cart, [count] item") : t("cart_count_other", "Cart, [count] items")).replace("[count]", n);
        btn.setAttribute("aria-label", label);
        if (n) btn.removeAttribute("data-empty"); else btn.setAttribute("data-empty", "");
      });
    }
    /* After any change: fetch the cart, re-render the sections, update the count. */
    function refresh(sections) {
      var drawer = d.getElementById("cart");
      var keep = focusMemo(drawer);
      return Promise.all([get(), render(sections)]).then(function (res) {
        var c = res[0];
        renderCount(c, true);
        if (drawer && layers.isOpen(drawer)) { drawer.classList.add("is-live"); keep(); }
        emit("vitalis:cart:updated", c);
        return c;
      });
    }
    /* Keep focus on the same control across a re-render (or a sensible neighbour). */
    function focusMemo(root) {
      var a = d.activeElement;
      if (!root || !a || !(a === root || a === d.body || root.contains(a))) return function () {};
      var key = a.getAttribute && a.getAttribute("data-key");
      var dir = a.getAttribute && a.getAttribute("data-dir");
      if (a === root || a === d.body) return function () { root.focus({ preventScroll: true }); };
      return function () {
        var q = function (sel) { return $(sel, root); };
        var k = key && (window.CSS && CSS.escape ? CSS.escape(key) : key);
        var target = (k && dir && q('[data-key="' + k + '"][data-dir="' + dir + '"]')) ||
          (k && q('[data-key="' + k + '"][data-dir="inc"]')) ||
          q('[data-action="dialog-close"]') || layers.focusables(root)[0] || root;
        if (target.disabled) target = (k && q('[data-key="' + k + '"][data-dir="inc"]')) || root;
        target.focus({ preventScroll: true });
      };
    }
    function sectionsParam() {
      var ids = sectionIds();
      return ids.length ? { sections: ids.join(","), sections_url: window.location.pathname } : {};
    }

    /* items: [{ id, quantity, properties }] or a FormData from a product form. */
    function add(items, o) {
      o = o || {};
      if (busy) return Promise.reject(new Error("busy"));
      busy = true;
      var body;
      var extra = sectionsParam();
      if (items instanceof FormData) {
        /* Sent url-encoded (a product form has no files), which every host parses. */
        body = new URLSearchParams();
        items.forEach(function (v, k) { if (typeof v === "string") body.append(k, v); });
        if (extra.sections) { body.append("sections", extra.sections); body.append("sections_url", extra.sections_url); }
      } else if (items.length === 1) {
        body = Object.assign({}, items[0], extra);        // one line: the flat form
      } else {
        body = Object.assign({ items: items }, extra);
      }
      return json(ROOT + "cart/add.js", body).then(function (res) {
        var added = res && res.items ? res.items[res.items.length - 1] : res;
        return refresh(res && res.sections).then(function (c) {
          busy = false;
          if (added && added.product_title) announce(t("added_announce", "[title] added to cart.").replace("[title]", added.product_title));
          if (o.open !== false) setTimeout(function () { openCart(o.opener, added); }, reduced() ? 0 : 300);
          return c;
        });
      }).catch(function (err) { busy = false; throw err; });
    }
    function change(key, quantity) {
      if (busy) return Promise.resolve();
      busy = true;
      $$('[data-cart-section] .cline[data-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]').forEach(function (l) { l.classList.add("is-busy"); });
      return json(ROOT + "cart/change.js", Object.assign({ id: key, quantity: quantity }, sectionsParam()))
        .then(function (res) { return refresh(res && res.sections); })
        .then(function (c) { busy = false; announce(t("cart_updated", "Cart updated.")); return c; })
        .catch(function (err) {
          busy = false;
          $$(".cline.is-busy").forEach(function (l) { l.classList.remove("is-busy"); });
          $$("[data-cart-status]").forEach(function (s) { s.textContent = err.message; s.classList.add("is-error"); });
          throw err;
        });
    }
    return { get: get, add: add, change: change, refresh: refresh, renderCount: renderCount };
  })();

  var TONE_SQ = { warm: "sq--warm", cold: "sq--cold", contrast: "sq--contrast" };
  function toneOfType(type) {
    var k = String(type || "").toLowerCase().replace(/[^a-z]+/g, "-");
    if (/^sauna/.test(k)) return "warm";
    if (/^(ice-bath|chiller|plunge|cold)/.test(k)) return "cold";
    if (/^(package|contrast)/.test(k)) return "contrast";
    return "neutral";
  }

  /* The drawer. added: the line item just added (a quiet "Added · name" line on top). */
  function openCart(opener, added) {
    var el = d.getElementById("cart");
    if (!el) { window.location.href = ROOT + "cart"; return; }
    var line = $(".cart-added", el);
    if (line) {
      if (added && added.product_title) {
        var opts = (added.options_with_values || []).filter(function (o) { return o.value !== "Default Title"; }).map(function (o) { return o.value; });
        var sq = TONE_SQ[toneOfType(added.product_type)];
        line.innerHTML = (sq ? '<span class="sq ' + sq + '" aria-hidden="true"></span>' : "") +
          esc(t("added", "Added")) + " · " + esc([added.product_title].concat(opts).join(" · "));
      } else line.innerHTML = "";
    }
    if (layers.isOpen(el)) { el.classList.add("is-live"); el.focus({ preventScroll: true }); return; }
    el.classList.remove("is-live");
    layers.open(el, {
      opener: opener || $("[data-cart-button]"),
      afterClose: function () { el.classList.remove("is-live"); var l = $(".cart-added", el); if (l) l.innerHTML = ""; }
    });
    setTimeout(function () { if (layers.isOpen(el)) el.classList.add("is-live"); }, reduced() ? 0 : 900);
  }

  /* ========================================================================
     7. TALK SHEET: WhatsApp and booking from Theme settings, plus the contact form.
     o: { about: product title, focus: "whatsapp" | "call" | "write", opener }
     ======================================================================== */
  function openTalk(o) {
    o = o || {};
    var el = d.getElementById("talk");
    if (!el) return;
    var about = o.about || "";
    var aboutLine = $("[data-talk-about]", el);
    if (aboutLine) aboutLine.textContent = about ? t("talk_about", "About: [title]").replace("[title]", about) : "";
    var wa = $('[data-talk="whatsapp"]', el);
    if (wa && wa.getAttribute("data-wa-base")) {
      var text = about ? t("talk_about", "About: [title]").replace("[title]", about) : t("talk_hello", "Hello Vitalis");
      wa.href = wa.getAttribute("data-wa-base") + "?text=" + encodeURIComponent(text);
    }
    var hidden = $("[data-talk-about-field]", el);
    if (hidden) hidden.value = about;
    var msg = $("#talk-message", el);
    if (msg && about && !msg.value) msg.value = t("talk_about", "About: [title]").replace("[title]", about) + "\n\n";
    var row = o.focus ? $('[data-talk="' + o.focus + '"]', el) : null;
    var into = row && o.focus === "write" ? $("#talk-name", el) : row;
    layers.open(el, { opener: o.opener, focus: into || null });
    if (row) {
      var body = $(".dlg__body", el);
      if (body) body.scrollTop = Math.max(0, row.offsetTop - 24);
    }
  }

  /* ========================================================================
     8. EMAIL SLIDE-IN: an app slot (email) with a native newsletter form. After the
     section's delay, or on desktop exit intent, whichever comes first. Once per
     visitor. Never over the menu, a drawer, a sheet or a page's buy bar: a trigger
     that lands then waits until that layer closes.
     ======================================================================== */
  var slidein = (function () {
    var pending = false, closeTimer = null, hideTimer = null, armed = false;
    function el() { return d.getElementById("slidein"); }
    function seen() { return store.get(KEYS.slidein) === "seen"; }
    function blocked() { return layers.isOpen() || html.classList.contains("has-buybar"); }
    function isOpen() { var e = el(); return !!e && !e.hidden && e.classList.contains("is-open"); }
    function show(force) {
      var e = el();
      if (!e || isOpen()) return false;
      if (!force && seen()) { pending = false; return false; }
      if (!force && blocked()) { pending = true; return false; }
      pending = false;
      store.set(KEYS.slidein, "seen");
      clearTimeout(hideTimer);
      e.hidden = false;
      void e.offsetWidth;
      e.classList.add("is-open");
      return true;
    }
    function hide() {
      var e = el();
      if (!e || e.hidden) return;
      var hadFocus = e.contains(d.activeElement);
      clearTimeout(closeTimer);
      e.classList.remove("is-open");
      hideTimer = setTimeout(function () { if (!e.classList.contains("is-open")) e.hidden = true; }, reduced() ? 0 : 420);
      if (hadFocus && main) main.focus({ preventScroll: true });
    }
    function submitted() { clearTimeout(closeTimer); closeTimer = setTimeout(hide, 3000); }
    function recheck() { if (pending) show(); }
    function arm() {
      var e = el();
      if (!e || armed) return;
      /* A form posted from here comes back with its message: show it again, once. */
      if (e.hasAttribute("data-posted") && window.location.hash === "#slidein-form") { show(true); submitted(); return; }
      /* In the theme editor it opens only while its section is selected (section 10). */
      if (seen() || (window.Shopify && window.Shopify.designMode)) return;
      armed = true;
      var delay = (parseInt(e.getAttribute("data-delay"), 10) || 20) * 1000;
      setTimeout(function () { show(); }, delay);
      if (e.getAttribute("data-exit") === "true" && media.fine.matches) {
        var onOut = function (ev) {
          if (ev.relatedTarget || ev.clientY > 0) return;
          d.removeEventListener("mouseout", onOut);
          show();
        };
        d.addEventListener("mouseout", onOut);
      }
    }
    d.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || e.defaultPrevented || !isOpen() || layers.isOpen()) return;
      e.preventDefault();
      hide();
    });
    return { arm: arm, show: show, hide: hide, recheck: recheck, submitted: submitted, isOpen: isOpen };
  })();

  /* ========================================================================
     9. EVENTS: one delegated click handler, form submits, the country select.
     ======================================================================== */
  function addedLook(btns) {
    btns.forEach(function (b) { b.classList.add("is-added"); });
    setTimeout(function () { btns.forEach(function (b) { b.classList.remove("is-added"); }); }, 1800);
  }
  function formStatus(form, msg) {
    var s = $("[data-form-status]", form) || $("[data-cart-status]");
    if (s) { s.textContent = msg; s.classList.add("is-error"); }
    announce(msg);
  }

  d.addEventListener("click", function (e) {
    var tgt = e.target;
    if (!(tgt instanceof Element)) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var a = tgt.closest("[data-action]");
    if (a) {
      var name = a.getAttribute("data-action");
      if (name === "cart") {
        if (!d.getElementById("cart")) return;            // no drawer: the link goes to /cart
        e.preventDefault();
        if (menu.isOpen()) menu.close({ restore: false });
        openCart(a);
        return;
      }
      if (name === "talk") {
        e.preventDefault();
        if (menu.isOpen()) menu.close({ restore: false });
        openTalk({ about: a.getAttribute("data-about"), focus: a.getAttribute("data-talk-focus"), opener: a });
        return;
      }
      if (name === "dialog-close") { e.preventDefault(); layers.close(); return; }
      if (name === "slidein-close") { e.preventDefault(); slidein.hide(); return; }
      if (name === "cart-qty") {
        e.preventDefault();
        cart.change(a.getAttribute("data-key"), parseInt(a.getAttribute("data-qty"), 10) || 0).catch(function () {});
        return;
      }
      if (name === "cart-add") {
        e.preventDefault();
        if (a.classList.contains("is-added")) return;
        var id = parseInt(a.getAttribute("data-variant-id"), 10);
        if (!id) return;
        addedLook([a]);
        cart.add([{ id: id, quantity: parseInt(a.getAttribute("data-quantity"), 10) || 1 }], { opener: a })
          .catch(function (err) { a.classList.remove("is-added"); if (err.message !== "busy") formStatus(a.closest("form") || d.body, err.message); });
        return;
      }
    }

    /* A menu link: close the menu first (the page then loads). */
    var link = tgt.closest("a[href]");
    if (!link) return;
    if (link.closest("#menu") && menu.isOpen()) menu.close({ restore: false });
    var hold = press.wait(link);
    if (hold > 0 && link.target !== "_blank" && link.origin === window.location.origin) {
      e.preventDefault();
      press.go(link.href, hold);
    }
  });

  function setError(input, msg) {
    var err = d.getElementById(input.id + "-err");
    if (msg) input.setAttribute("aria-invalid", "true"); else input.removeAttribute("aria-invalid");
    if (err) err.textContent = msg || "";
    return !msg;
  }

  d.addEventListener("submit", function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    /* Product forms: add over AJAX, then the drawer. */
    var action = form.getAttribute("action") || "";
    if (/\/cart\/add(\.js)?$/.test(action.split("?")[0]) && !form.hasAttribute("data-no-ajax") && window.fetch && window.FormData) {
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]') || e.submitter;
      var btns = [btn].concat($$('[form="' + form.id + '"][type="submit"]')).filter(Boolean);
      if (btn && btn.classList.contains("is-added")) return;
      var fs = $("[data-form-status]", form);
      if (fs) { fs.textContent = ""; fs.classList.remove("is-error"); }
      addedLook(btns);
      cart.add(new FormData(form), { opener: btn })
        .catch(function (err) {
          btns.forEach(function (b) { b.classList.remove("is-added"); });
          if (err.message !== "busy") formStatus(form, err.message);
        });
      return;
    }

    /* Newsletter and contact forms: check in place, then let Shopify take them. */
    var email = form.querySelector('input[type="email"]');
    if (form.hasAttribute("data-validate") && email) {
      var ok = setError(email, EMAIL_RE.test(email.value.trim()) ? "" : t("invalid_email", "Enter an email address."));
      var name = form.querySelector("[data-required-name]");
      var okName = name ? setError(name, name.value.trim() ? "" : t("required_name", "Enter your name.")) : true;
      if (!ok || !okName) {
        e.preventDefault();
        (!okName ? name : email).focus();
      }
    }
  });

  /* Clear an inline error as soon as the field is corrected. */
  d.addEventListener("input", function (e) {
    var el = e.target;
    if (!el || !el.id || el.getAttribute("aria-invalid") !== "true") return;
    var ok = el.type === "email" ? EMAIL_RE.test(el.value.trim()) : !!el.value.trim();
    if (ok) setError(el, "");
  });

  /* Country form: posts on its Update button only (WCAG 3.2.2); boot refocuses the select. */
  d.addEventListener("submit", function (e) {
    var s = e.target instanceof HTMLFormElement && $("[data-loc-select]", e.target);
    if (s) store.set(KEYS.loc, s.id);
  });

  /* ========================================================================
     10. THEME EDITOR: re-init a reloaded section; open the drawer, the sheet or the
     slide-in while its section is selected.
     ======================================================================== */
  function layerOf(target) {
    return target && (target.querySelector("#cart, #talk") || null);
  }
  d.addEventListener("shopify:section:load", function (e) {
    reveal.init(e.target, "intro");
    if (e.target.querySelector("#hdr")) observeHeader();
  });
  d.addEventListener("shopify:section:select", function (e) {
    var l = layerOf(e.target);
    if (l && l.id === "cart") openCart();
    else if (l && l.id === "talk") openTalk();
    else if (e.target.querySelector("#slidein")) slidein.show(true);
  });
  d.addEventListener("shopify:section:deselect", function (e) {
    if (layerOf(e.target)) layers.close({ restore: false });
    if (e.target.querySelector("#slidein")) slidein.hide();
  });

  /* ========================================================================
     11. PUBLIC API + BOOT
     ======================================================================== */
  window.Vitalis = {
    openCart: function (opener) { openCart(opener); },
    closeCart: function () { var el = d.getElementById("cart"); if (el && layers.isOpen(el)) layers.close(); },
    refreshCart: function () { return cart.refresh(); },
    addToCart: function (items, o) { return cart.add(Array.isArray(items) ? items : [items], o); },
    changeLine: function (key, qty) { return cart.change(key, qty); },
    getCart: cart.get,
    openTalk: openTalk,
    openMenu: menu.open,
    closeMenu: menu.close,
    openSlidein: function (force) { return slidein.show(force !== false); },
    closeSlidein: slidein.hide,
    layers: layers,
    reveal: reveal,
    announce: announce,
    store: store,
    toneOfType: toneOfType
  };

  observeHeader();
  reveal.init(d, "intro");
  var loc = store.get(KEYS.loc);
  if (loc) {
    store.set(KEYS.loc, "");
    if (loc === "loc-menu" && !media.desktop.matches) menu.open(d.getElementById(loc));
    else if (d.getElementById("loc-footer")) d.getElementById("loc-footer").focus();
  }
  slidein.arm();
  /* A contact form posted from the Talk sheet comes back with its message: reopen it. */
  var talk = d.getElementById("talk");
  if (talk && talk.hasAttribute("data-open-on-load")) {
    openTalk({ focus: "write" });
    var st = $("#talk-status", talk);
    if (st && st.scrollIntoView) st.scrollIntoView({ block: "nearest" });
  }
})();
